import React, {useState} from 'react';

import {
  addToast,
  Button,
  ButtonGroup,
  Heading,
  Text,
  Input,
  TextArea,
  Modal,
  Dialogue
} from '@albert-io/atomic';

import sessionStore from 'client/Session/SessionStore';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import type {SubjectFaQsQuery} from '../SubjectFAQList.queries.generated';
import {
  useCreateSubjectFaqMutation,
  useUpdateSubjectFaqMutation,
  useDestroySubjectFaqMutation,
  SubjectFaQsDocument
} from '../SubjectFAQList.queries.generated';

type SubjectFaq = NonNullable<
  NonNullable<SubjectFaQsQuery['subjects']>['results']
>[number]['subjectFaqs'][number];

interface SubjectFAQItemProps {
  subjectFAQ: SubjectFaq;
  subjectId: string;
  slug: string;
  isCreatingNew?: boolean;
  onDiscardNew?: () => void;
}

const SubjectFAQItem = ({
  subjectFAQ,
  subjectId,
  slug,
  isCreatingNew = false,
  onDiscardNew
}: SubjectFAQItemProps) => {
  const [isEditMode, setIsEditMode] = useState(isCreatingNew);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [question, setQuestion] = useState(subjectFAQ.question || '');
  const [answer, setAnswer] = useState(subjectFAQ.answer || '');

  const refetchConfig = {refetchQueries: [{query: SubjectFaQsDocument, variables: {slug}}]};

  const [createFaq] = useCreateSubjectFaqMutation(refetchConfig);
  const [updateFaq] = useUpdateSubjectFaqMutation(refetchConfig);
  const [destroyFaq] = useDestroySubjectFaqMutation(refetchConfig);

  // Intentionally trims whitespace (improvement over legacy which only checked length)
  const isNotValidFAQ = () => answer.trim().length === 0;

  const saveFAQ = async () => {
    const trimmedQuestion = question.trim();
    const trimmedAnswer = answer.trim();

    try {
      if (isCreatingNew) {
        const {data: createData} = await createFaq({
          variables: {subjectId, question: trimmedQuestion, answer: trimmedAnswer}
        });
        if (createData?.createSubjectFaq?.errors?.length) {
          throw new Error(createData.createSubjectFaq.errors[0]?.message ?? 'Validation error');
        }
        addToast({
          color: 'positive',
          title: 'Subject FAQ Saved!',
          message: 'This FAQ has successfully been added!'
        });
        onDiscardNew?.();
      } else {
        const {data: updateData} = await updateFaq({
          variables: {id: subjectFAQ.id, question: trimmedQuestion, answer: trimmedAnswer}
        });
        if (updateData?.updateSubjectFaq?.errors?.length) {
          throw new Error(updateData.updateSubjectFaq.errors[0]?.message ?? 'Validation error');
        }
        setIsEditMode(false);
        addToast({
          color: 'positive',
          title: 'Subject FAQ Saved!',
          message: 'This FAQ has successfully been updated!'
        });
      }
    } catch {
      addToast({
        color: 'negative',
        title: 'Subject FAQ Save Failed!',
        message: 'Subject FAQ Save Failed.'
      });
    }
  };

  const deleteFAQ = async () => {
    if (isCreatingNew) {
      onDiscardNew?.();
      return;
    }
    try {
      const {data: destroyData} = await destroyFaq({variables: {id: subjectFAQ.id}});
      if (destroyData?.destroySubjectFaq?.errors?.length) {
        throw new Error(destroyData.destroySubjectFaq.errors[0]?.message ?? 'Delete failed');
      }
      addToast({
        color: 'positive',
        title: 'Subject FAQ has been deleted.',
        message: 'Subject FAQ has been deleted'
      });
    } catch {
      addToast({
        color: 'negative',
        title: 'Unable to delete FAQ.',
        message: 'Unable to delete FAQ.'
      });
    }
  };

  const deleteConfirmationModal = isDeleteModalVisible ? (
    <Modal
      ariaLabel='Delete FAQ'
      dismissable
      handleClose={() => setIsDeleteModalVisible(false)}
      role='dialog'
    >
      {({CloseButtonWrapper, modalContentStyle}) => (
        <Dialogue
          className={modalContentStyle}
          handleClose={() => setIsDeleteModalVisible(false)}
          inModal
          title='Delete FAQ'
        >
          <Dialogue.Body>
            {isCreatingNew ? (
              'Are you sure you want to delete this FAQ without saving?'
            ) : (
              <>
                Are you sure you want to delete the question:{' '}
                <Text bold>&quot;{subjectFAQ.question}&quot;</Text>
              </>
            )}
          </Dialogue.Body>
          <Dialogue.BtnGroup>
            <CloseButtonWrapper>
              <Button color='secondary'>Cancel</Button>
            </CloseButtonWrapper>
            <Button color='negative' onClick={deleteFAQ} type='button'>
              Delete
            </Button>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  ) : null;

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsEditMode(true);
    }
  };

  const isAdmin = sessionStore.hasContentManagerAccess();

  const readFields = (
    <div
      id={subjectFAQ.id}
      onClick={isAdmin ? () => setIsEditMode(true) : undefined}
      onKeyDown={isAdmin ? handleEditKeyDown : undefined}
      role={isAdmin ? 'button' : undefined}
      tabIndex={isAdmin ? 0 : undefined}
    >
      {question && (
        <Heading as='h3' size='s' className={isAdmin ? 'cursor-pointer' : undefined}>
          {question}
        </Heading>
      )}
      <Text className={isAdmin ? 'cursor-pointer' : undefined}>
        <MarkdownRendererV2 text={answer} />
      </Text>
    </div>
  );

  const editFields = (
    <div>
      <Input
        type='text'
        autoComplete='off'
        placeholder='Question'
        name='question'
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
      />
      <TextArea
        autoComplete='off'
        placeholder='Answer'
        name='answer'
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={15}
      />
    </div>
  );

  const adminButtons = sessionStore.hasContentManagerAccess() ? (
    <ButtonGroup align='right'>
      {isEditMode ? (
        <Button
          size='s'
          color='secondary'
          onClick={() => setIsEditMode(false)}
          disabled={isNotValidFAQ()}
        >
          Preview
        </Button>
      ) : null}
      <Button size='s' color='negative' onClick={() => setIsDeleteModalVisible(true)}>
        Delete FAQ
      </Button>
      <Button size='s' color='secondary' onClick={saveFAQ} disabled={isNotValidFAQ()}>
        Save + Publish
      </Button>
      {deleteConfirmationModal}
    </ButtonGroup>
  ) : null;

  return (
    <div className='my-x2 rounded-x1 border border-solid border-border-primary background-primary p-x2'>
      {sessionStore.hasContentManagerAccess() && isEditMode ? editFields : readFields}
      {adminButtons}
    </div>
  );
};

export default SubjectFAQItem;
